/**
 * Casca MOBILE do formulário público de Percepção de Riscos (IMGR), estilo
 * Typeform: capa → identificação → intro do bloco + uma afirmativa por tela →
 * conclusão. Auto-avança ao responder e retoma de onde parou. Visual do design
 * aprovado; dados, escala e envio reais do sistema.
 */
import { CARGOS } from "@caint/data";
import type { FormularioPublico } from "@caint/shared";
import { useEffect, useRef, useState } from "react";
import {
  ESCALA_TOPO,
  type Enviar,
  Icone,
  type PerguntaUi,
  SECOES,
  type SecaoUi,
  TOTAL,
  cargosPorArea,
  corEscala,
} from "./percepcao.tsx";

type Passo =
  | { t: "cover" }
  | { t: "ident" }
  | { t: "secintro"; sec: SecaoUi; si: number }
  | { t: "q"; sec: SecaoUi; si: number; p: PerguntaUi }
  | { t: "done" };

function montarPassos(): Passo[] {
  const passos: Passo[] = [{ t: "cover" }, { t: "ident" }];
  SECOES.forEach((sec, si) => {
    passos.push({ t: "secintro", sec, si });
    for (const p of sec.perguntas) passos.push({ t: "q", sec, si, p });
  });
  passos.push({ t: "done" });
  return passos;
}

export function PercepcaoMobile({
  meta,
  enviar,
  enviando,
}: {
  meta: FormularioPublico;
  enviar: Enviar;
  enviando: boolean;
}) {
  const chave = `caint-percepcao-${meta.token}`;
  const salvo = (() => {
    try {
      return JSON.parse(localStorage.getItem(chave) ?? "{}");
    } catch {
      return {};
    }
  })();
  const passos = useRef(montarPassos()).current;
  // Cargos da unidade (da matriz), com fallback no catálogo global.
  const cargos = useRef(meta.cargos?.length ? meta.cargos : CARGOS).current;
  const areas = useRef(cargosPorArea(cargos)).current;
  const [idx, setIdx] = useState<number>(salvo.idx ?? 0);
  const [ans, setAns] = useState<Record<string, number>>(salvo.ans ?? {});
  const [cargoIdx, setCargoIdx] = useState<number | null>(salvo.cargoIdx ?? null);
  const [setor, setSetor] = useState<string>(salvo.setor ?? "");
  const [resultado, setResultado] = useState<"idle" | "ok" | "erro">("idle");
  const [erro, setErro] = useState<string | null>(null);
  const enviou = useRef(false);

  useEffect(() => {
    try {
      localStorage.setItem(chave, JSON.stringify({ idx, ans, cargoIdx, setor }));
    } catch {
      // armazenamento indisponível — segue em memória
    }
  }, [chave, idx, ans, cargoIdx, setor]);

  const passo: Passo = passos[idx] ?? passos[0] ?? { t: "cover" };
  const respondidas = Object.keys(ans).length;
  const pct = Math.round((respondidas / TOTAL) * 100);
  const completo = respondidas === TOTAL && cargoIdx != null;
  const sel = cargoIdx != null ? cargos[cargoIdx] : null;

  function ir(n: number) {
    setIdx((i) => Math.max(0, Math.min(passos.length - 1, i + n)));
  }
  function responder(num: number, v: number) {
    setAns((a) => ({ ...a, [String(num)]: v }));
    setTimeout(() => setIdx((i) => Math.min(passos.length - 1, i + 1)), 220);
  }

  async function finalizar() {
    if (cargoIdx == null) return;
    const c = cargos[cargoIdx];
    if (!c) return;
    setErro(null);
    const r = await enviar({
      cargo: c.cargo,
      nivel: c.nivel,
      area: c.area,
      perfil: c.perfil,
      setor: setor.trim() || undefined,
      respostas: ans,
    });
    if (r === "ok") {
      setResultado("ok");
      try {
        localStorage.removeItem(chave);
      } catch {
        // ignore
      }
    } else if (typeof r === "object") {
      enviou.current = false;
      setResultado("erro");
      setErro(r.erro);
    }
    // "fechado" → a casca orquestradora troca de tela.
  }

  // Envia automaticamente ao chegar na conclusão com tudo respondido.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `finalizar` fica fora de propósito — o envio dispara por mudança de estado (passo/completo/resultado); incluí-lo (recriado a cada render) reenviaria em loop após erro.
  useEffect(() => {
    if (passo.t === "done" && completo && !enviou.current && resultado !== "ok") {
      enviou.current = true;
      void finalizar();
    }
  }, [passo.t, completo, resultado]);

  // ---------- CAPA ----------
  if (passo.t === "cover") {
    return (
      <div className="f-app">
        <div className="f-cover">
          <div className="f-cover-top">
            <div className="eyebrow">Percepção de Riscos · CAINT</div>
            <h1>Como você enxerga os riscos na sua unidade?</h1>
            <p>
              São {TOTAL} afirmativas curtas. Para cada uma, diga o quanto concorda com o que acontece no dia
              a dia. Não existe resposta certa — queremos a sua percepção sincera.
            </p>
            <div className="f-facts">
              <div className="f-fact">
                <span className="fi">
                  <Icone nome="clock" s={20} />
                </span>
                <div>
                  <b>Leva cerca de 5 minutos</b>
                  <span>Suas respostas ficam salvas se precisar parar.</span>
                </div>
              </div>
              <div className="f-fact">
                <span className="fi">
                  <Icone nome="lock" s={20} />
                </span>
                <div>
                  <b>Respostas confidenciais</b>
                  <span>Usadas só para entender a cultura de risco da unidade.</span>
                </div>
              </div>
              <div className="f-fact">
                <span className="fi">
                  <Icone nome="heart" s={20} />
                </span>
                <div>
                  <b>Sem resposta certa ou errada</b>
                  <span>Responda pensando no que realmente acontece.</span>
                </div>
              </div>
            </div>
          </div>
          <button type="button" className="f-btn primary" onClick={() => ir(1)} style={{ marginTop: 24 }}>
            Começar
          </button>
          <div style={{ textAlign: "center", marginTop: 14, fontSize: 12.5, color: "rgba(255,255,255,0.5)" }}>
            {meta.unidadeNome}
          </div>
        </div>
      </div>
    );
  }

  // ---------- IDENTIFICAÇÃO ----------
  if (passo.t === "ident") {
    return (
      <div className="f-app">
        <FTopo pct={pct} secNome="Identificação" count="Antes de começar" onBack={() => ir(-1)} />
        <div className="f-body">
          <div className="f-anim" key={idx}>
            <div className="f-q-eyebrow">Quem está respondendo</div>
            <h2 className="f-q-title">Qual é o seu cargo?</h2>
            <div className="f-scalehint">Mantemos a resposta anônima — só usamos cargo e setor.</div>
            <label className="f-lbl" htmlFor="m-cargo">
              Cargo
            </label>
            <select
              id="m-cargo"
              className="f-select"
              value={cargoIdx ?? ""}
              onChange={(e) => setCargoIdx(e.target.value === "" ? null : Number(e.target.value))}
            >
              <option value="" disabled>
                Selecione o cargo…
              </option>
              {areas.map(([area, itens]) => (
                <optgroup key={area} label={area}>
                  {itens.map(({ c, idx: i }) => (
                    <option key={i} value={i}>
                      {c.cargo}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            {sel && (
              <div className="f-chips">
                <span className="f-chip">
                  Nível <b>{sel.nivel}</b>
                </span>
                <span className="f-chip">
                  Área <b>{sel.area}</b>
                </span>
                <span className="f-chip">
                  Perfil <b>{sel.perfil}</b>
                </span>
              </div>
            )}
            <label className="f-lbl" htmlFor="m-setor" style={{ marginTop: 16 }}>
              Setor de lotação (opcional)
            </label>
            <input
              id="m-setor"
              className="f-input"
              type="text"
              maxLength={80}
              value={setor}
              placeholder="Ex.: Centro Obstétrico"
              onChange={(e) => setSetor(e.target.value)}
            />
          </div>
        </div>
        <div className="f-foot">
          <button type="button" className="f-btn primary" disabled={cargoIdx == null} onClick={() => ir(1)}>
            Continuar
          </button>
        </div>
      </div>
    );
  }

  // ---------- CONCLUSÃO ----------
  if (passo.t === "done") {
    if (!completo) {
      const faltam = TOTAL - respondidas;
      const primeira = passos.findIndex((s) => s.t === "q" && ans[String(s.p.num)] == null);
      return (
        <div className="f-app">
          <div className="f-done">
            <div className="ring" style={{ background: "var(--warning-soft)", color: "var(--warning)" }}>
              <Icone nome="arrow" s={40} w={2.4} />
            </div>
            <h2>Quase lá!</h2>
            <p>
              Faltam {faltam} {faltam === 1 ? "afirmativa" : "afirmativas"} para concluir. Suas respostas
              ficaram salvas.
            </p>
            <button
              type="button"
              className="f-btn primary"
              style={{ marginTop: 22 }}
              onClick={() => setIdx(primeira >= 0 ? primeira : 1)}
            >
              Continuar respondendo
            </button>
          </div>
        </div>
      );
    }
    if (resultado === "erro") {
      return (
        <div className="f-app">
          <div className="f-done">
            <div className="ring" style={{ background: "var(--danger-soft)", color: "var(--danger)" }}>
              <Icone nome="back" s={40} w={2.4} />
            </div>
            <h2>Não foi possível enviar</h2>
            <p>{erro ?? "Houve um erro de conexão."}</p>
            <button
              type="button"
              className="f-btn primary"
              style={{ marginTop: 22 }}
              disabled={enviando}
              onClick={() => {
                enviou.current = true;
                void finalizar();
              }}
            >
              {enviando ? "Enviando…" : "Tentar novamente"}
            </button>
          </div>
        </div>
      );
    }
    if (resultado !== "ok") {
      return (
        <div className="f-app">
          <div className="f-done">
            <div className="ring">
              <Icone nome="clock" s={40} w={2.2} />
            </div>
            <h2>Enviando suas respostas…</h2>
            <p>Só um instante.</p>
          </div>
        </div>
      );
    }
    return (
      <div className="f-app">
        <div className="f-done">
          <div className="ring">
            <Icone nome="check" s={44} w={2.4} />
          </div>
          <h2>Obrigado pela sua percepção!</h2>
          <p>
            Suas {respondidas} respostas foram registradas e ajudam a entender a cultura de risco e segurança
            da unidade.
          </p>
          <div className="f-summary">
            <div className="sr">
              <span style={{ color: "var(--text-muted)" }}>Afirmativas respondidas</span>
              <b>
                {respondidas} de {TOTAL}
              </b>
            </div>
            <div className="sr">
              <span style={{ color: "var(--text-muted)" }}>Blocos</span>
              <b>{SECOES.length}</b>
            </div>
            <div className="sr">
              <span style={{ color: "var(--text-muted)" }}>Unidade</span>
              <b style={{ maxWidth: 170, textAlign: "right" }}>{meta.unidadeNome}</b>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------- INTRO DO BLOCO ----------
  if (passo.t === "secintro") {
    return (
      <div className="f-app">
        <FTopo
          pct={pct}
          secNome={passo.sec.nome}
          count={`Bloco ${passo.si + 1} de ${SECOES.length}`}
          onBack={() => ir(-1)}
        />
        <div className="f-body">
          <div className="f-secintro f-anim" key={idx}>
            <div className="badge-step">
              Bloco {passo.si + 1} de {SECOES.length}
            </div>
            <div className="f-secicon" style={{ background: passo.sec.cor }}>
              <Icone nome={passo.sec.icone} s={42} />
            </div>
            <h2>{passo.sec.nome}</h2>
            <p>{passo.sec.intro}</p>
            <div style={{ marginTop: 18, fontSize: 13, color: "var(--text-faint)", fontWeight: 600 }}>
              {passo.sec.perguntas.length} afirmativas
            </div>
          </div>
        </div>
        <div className="f-foot">
          <button type="button" className="f-btn primary" onClick={() => ir(1)}>
            Continuar
          </button>
        </div>
      </div>
    );
  }

  // ---------- AFIRMATIVA ----------
  const { p, sec } = passo;
  const val = ans[String(p.num)];
  return (
    <div className="f-app">
      <FTopo pct={pct} secNome={sec.nome} count={`Afirmativa ${p.num} de ${TOTAL}`} onBack={() => ir(-1)} />
      <div className="f-body">
        <div className="f-anim" key={idx}>
          <div className="f-q-eyebrow" style={{ color: sec.cor }}>
            {sec.nome}
          </div>
          <h2 className="f-q-title">{p.texto}</h2>
          <div className="f-scalehint">O quanto você concorda?</div>
          <div className="f-options">
            {ESCALA_TOPO.map((o) => (
              <button
                type="button"
                key={o.valor}
                className={`f-opt${val === o.valor ? " sel" : ""}`}
                style={{ ["--clr" as string]: corEscala(o.valor) }}
                onClick={() => responder(p.num, o.valor)}
              >
                <span className="f-bar" />
                <span className="f-main">
                  <span className="f-t">{o.rotulo}</span>
                  <span className="f-d">{o.desc}</span>
                </span>
                <span className="f-check">{val === o.valor && <Icone nome="check" s={15} w={3} />}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="f-foot">
        <button type="button" className="f-btn ghost" onClick={() => ir(1)}>
          {val != null ? "Avançar" : "Responder depois"}
        </button>
      </div>
    </div>
  );
}

function FTopo({
  pct,
  secNome,
  count,
  onBack,
}: {
  pct: number;
  secNome: string;
  count: string;
  onBack: () => void;
}) {
  return (
    <div className="f-top">
      <div className="f-top-row">
        <button type="button" className="f-back" onClick={onBack}>
          <Icone nome="backsm" s={22} />
        </button>
        <div className="f-top-meta">
          <div className="ft-sec">{secNome}</div>
          <div className="ft-count">{count}</div>
        </div>
        <div
          style={{ fontFamily: "var(--font-mono)", fontSize: 13, fontWeight: 700, color: "var(--primary)" }}
        >
          {pct}%
        </div>
      </div>
      <div className="f-progress">
        <i style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
